import { InfoCard, Text, VStack } from '@ayble-health/dashboard-components';
import { type ReactNode } from 'react';
import { Box } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';

import { useUserMeetings } from '../../api/healthCoach/useUserMeetings';
import { MeetingStatusBadge } from './MeetingStatusBadge';
import {
  computeMeetingsToDisplay,
  formatDateInBrowserTZ,
  getMeetingGoalsByMeetingType,
  isMeetingJoinable,
  isWithin24HoursOfMeetingStart,
} from './utils';
import { useUserFeedbackSurvey } from '../../api/healthCoach/useUserFeedback';
import MeetingCardEditor from './MeetingCardEditor/MeetingCardEditor';
import { Loader } from '../Loader';
import { ChartNote, MeetingNote } from '../../api/types';
import UpcomingMeetingChartNotes from './UpcomingMeetingChartNotes';
import { UserFeedbackSurvey } from '../../models/userFeedback';

type Props = {
  onOpenChartNote: (chartingNoteUrl: string | null) => void;
};

type MeetingCardProps = {
  meeting: MeetingNote;
  surveys: UserFeedbackSurvey[];
  onOpenChartNote: (chartNote: ChartNote) => void;
  showJoinButton?: boolean;
};

const MeetingSectionHeader = ({ children }: { children: ReactNode }) => (
  <Text
    color={'blueGray.600'}
    fontSize={'md'}
    fontWeight={'normal'}
    textAlign="left"
    w="100%"
  >
    {children}
  </Text>
);

const MeetingCard = ({
  meeting,
  surveys,
  onOpenChartNote,
  showJoinButton = true,
}: MeetingCardProps) => {
  const {
    client_confirmed,
    meeting_date,
    meeting_type,
    provider,
    pm_status,
    zoom_start_url,
  } = meeting;

  const { display_name, image_url } = provider || {};

  const meetingGoals = getMeetingGoalsByMeetingType(
    surveys,
    meeting_type ?? ''
  );

  const handlePrimaryButtonClick = () => {
    if (!zoom_start_url) return;

    window.open(zoom_start_url, '_blank')?.focus();
  };

  return (
    <InfoCard
      avatarUrl={image_url}
      borderTopRadius={'md'}
      borderBottomRadius={'xl'}
      content={
        <Text color={'blueGray.600'} fontSize={'md'} fontWeight={'normal'}>
          <>
            <MeetingStatusBadge
              isConfirmed={client_confirmed}
              pmStatus={pm_status}
            />
            {meetingGoals ? `"${meetingGoals}"` : 'No visit notes.'}
          </>
        </Text>
      }
      headingPill={formatDateInBrowserTZ(meeting_date)}
      onPrimaryButtonClick={
        showJoinButton ? handlePrimaryButtonClick : undefined
      }
      primaryButtonDisabled={!isWithin24HoursOfMeetingStart(meeting_date)}
      primaryButtonText="Join Visit"
      subtitle={display_name}
      stretchButtons
      title={meeting_type ?? ''}
      titleSize={'19px'}
      w="100%"
    >
      <UpcomingMeetingChartNotes
        chartNotes={meeting.chart_notes}
        onClick={onOpenChartNote}
      />
      <MeetingCardEditor meeting={meeting} />
    </InfoCard>
  );
};

export const UpcomingMeeting = ({ onOpenChartNote }: Props) => {
  const { patient } = useParams();
  const { meetings, isLoading, isError } = useUserMeetings(patient ?? '');
  const { userFeedbackSurveyData } = useUserFeedbackSurvey();
  const { nextUp, toBeCompleted } = computeMeetingsToDisplay(meetings ?? []);

  if (isLoading) {
    return (
      <Box p={0}>
        <Loader loading={true} />
      </Box>
    );
  }

  if (isError || (!nextUp && toBeCompleted.length === 0)) return null;

  const handleOpenChartNote = ({ note_url }: ChartNote) => {
    if (note_url) onOpenChartNote(note_url);
  };

  const surveys = userFeedbackSurveyData ?? [];

  return (
    <VStack alignItems="stretch" gap={4} my={6} w="100%">
      {nextUp && (
        <VStack alignItems="stretch" gap={2} w="100%">
          <MeetingSectionHeader>Next Up</MeetingSectionHeader>
          <MeetingCard
            meeting={nextUp}
            surveys={surveys}
            onOpenChartNote={handleOpenChartNote}
          />
        </VStack>
      )}

      {toBeCompleted.length > 0 && (
        <VStack alignItems="stretch" gap={2} w="100%">
          <MeetingSectionHeader>Past Visits (to be completed)</MeetingSectionHeader>
          {toBeCompleted.map((meeting) => (
            <MeetingCard
              key={meeting.healthie_appointment_id}
              meeting={meeting}
              surveys={surveys}
              onOpenChartNote={handleOpenChartNote}
              showJoinButton={isMeetingJoinable(meeting.meeting_date)}
            />
          ))}
        </VStack>
      )}
    </VStack>
  );
};
